<template>
  <v-dialog v-model="show" scrollable persistent max-width="900">
    <v-card flat>
      <v-toolbar color="primaryLight1" flat dark height="50px">
        <span>{{ $t("lblTenantsOrganizer") }}</span>
        <v-spacer></v-spacer>
        <CloseIcon @click="onClose" />
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="5" sm="5">
            <v-card height="350" style="overflow: auto">
              <v-list dense>
                <v-list-item-group
                  v-model="selectedTenant"
                  color="primary"
                  multiple
                >
                  <v-list-item
                    v-for="tenant in tenantAuthList"
                    :key="tenant.id"
                    :value="tenant"
                    :disabled="tenant.isMoved"
                    dense
                    link
                  >
                    <v-list-item-action class="mr-2">
                      <v-icon size="20" color="neutralGray">mdi-domain</v-icon>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title class="text-truncate">{{
                        tenant.name
                      }}</v-list-item-title>
                    </v-list-item-content>

                    <v-list-item-action v-if="tenant.isMoved" class="mr-2">
                      <v-chip
                        x-small
                        label
                        class="ma-2"
                        color="grey"
                        style="max-width: 80px; overflow: hidden"
                      >
                        <span class="chip-text-truncate">
                          {{ getGroupNameByTenantId(tenant.id) }}
                        </span>
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-card>
          </v-col>
          <v-col
            cols="2"
            sm="2"
            class="d-flex flex-column justify-center align-center"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <a-btn
                  color="primaryDark"
                  v-bind="attrs"
                  v-on="on"
                  @click="onAddGroup"
                  class="mb-2"
                  small
                >
                  <v-icon class="ml-2" size="16">mdi-plus</v-icon>
                  {{ $t("lblGroup") }}
                </a-btn>
              </template>
              <span>{{ $t("tipNewGroup") }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <a-btn
                  :disabled="isAddButtonDisabled"
                  color="primaryDark"
                  v-bind="attrs"
                  v-on="on"
                  @click="onMovieTenant"
                  small
                >
                  {{ $t("btnAdd") }}
                  <v-icon class="ml-2" size="16">mdi-transfer-right</v-icon>
                </a-btn>
              </template>
              <span>{{ $t("tipSelectedTenants") }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="5" sm="5">
            <v-card height="350" style="overflow: auto">
              <v-treeview
                :active.sync="selectedGroupTenant"
                hoverable
                activatable
                :items="tenantList"
                item-key="id"
                item-text="name"
                item-children="tenants"
                item-disabled="disabled"
              >
                <template v-slot:prepend="{ item }">
                  <v-icon v-if="item.tenants" size="20" color="neutralGray">
                    mdi-folder</v-icon
                  >
                  <v-icon v-else size="20" color="neutralGray"
                    >mdi-domain</v-icon
                  >
                </template>
                <template v-slot:append="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="item.tenants"
                        v-bind="attrs"
                        v-on="on"
                        icon
                        small
                        @click.stop="onEditGroup(item)"
                      >
                        <v-icon small color="neutralGray1">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("btnEdit") }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        small
                        @click.stop="onDelete(item)"
                        class="mr-2"
                      >
                        <v-icon small color="neutralGray1">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("btnDelete") }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <a-btn
                        v-bind="attrs"
                        v-on="on"
                        class="arrow-up-thick-left"
                        x-small
                        color="primaryDark"
                        width="24"
                        min-width="24"
                        @click.stop="moveUp(item)"
                        :disabled="isFirstItem(item)"
                      >
                        <v-icon size="16" color="#fff">
                          mdi-arrow-up-thick</v-icon
                        >
                      </a-btn>
                    </template>
                    <span>{{ $t("lblMoveUp") }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <a-btn
                        v-bind="attrs"
                        v-on="on"
                        class="arrow-down-thick-right ml-1"
                        x-small
                        color="primaryDark"
                        width="24"
                        min-width="24"
                        @click.stop="moveDown(item)"
                        :disabled="isLastItem(item)"
                      >
                        <v-icon size="16" color="#fff">
                          mdi-arrow-down-thick</v-icon
                        >
                      </a-btn>
                    </template>
                    <span>{{ $t("lblMoveDown") }}</span>
                  </v-tooltip>
                </template>
              </v-treeview>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              :disabled="!isDirty"
              v-bind="attrs"
              v-on="on"
              class="mr-2 primaryDark"
              @click="onSave()"
              >{{ $t("btnSave") }}</a-btn
            >
          </template>
          <span>{{ $t("btnSave") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-bind="attrs"
              v-on="on"
              class="secondary"
              @click="onClose()"
              >{{ $t("btnLabelCancel") }}</a-btn
            >
          </template>
          <span>{{ $t("btnLabelCancel") }}</span>
        </v-tooltip>
      </v-card-actions>
    </v-card>
    <add-group
      v-if="groupForm"
      v-model="showAddGroupDialog"
      @addGroup="onGroupAddUpdate"
      :groupForm="groupForm"
      :groupList="groupList"
    ></add-group>
  </v-dialog>
</template>

<script>
import AddGroup from "./AddGroup.vue";
import { createUUID } from "../../utils/util";
import { mapActions, mapState } from "vuex";
import { cloneDeep } from "lodash";
import { blankObjectConvertToArray } from "@/utils/util";

export default {
  components: { AddGroup },
  name: "TenantListOrganizer",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      refTenantList: [],
      isErrorShow: false,
      groupForm: null,
      selectedGroupTenant: [],
      selectedTenant: [],
      showAddGroupDialog: false,
      admins: [
        ["Management", "mdi-account-multiple-outline"],
        ["Settings", "mdi-cog-outline"],
      ],
      cruds: [
        ["Create", "mdi-plus-outline"],
        ["Read", "mdi-file-outline"],
        ["Update", "mdi-update"],
        ["Delete", "mdi-delete"],
      ],
      tenantList: [],
      movedTenantsId: [],
    };
  },
  mounted() {
    if (this.tenantGroupingList) {
      this.tenantList = this.transformTenantGroupData(this.tenantGroupingList);
    } else {
      this.tenantList = this.transformTenantGroupData(
        this.$auth.user.tenant_grouping_list
      );
    }
    this.refTenantList = cloneDeep(this.tenantList);
  },
  computed: {
    ...mapState({
      tenantGroupingList: (state) => state.admin.tenantGroupingList,
    }),
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value || false);
      },
    },
    tenantAuthList() {
      const tenants = this.$auth.user.tenant_grouping_list.tenants || [];
      const movedIds = this.movedTenantsId || [];

      return tenants.map((tenant) => ({
        ...tenant,
        isMoved: movedIds.includes(tenant.id),
        disabled: true,
      }));
    },
    isAddButtonDisabled() {
      return !(this.selectedGroupTenant.length && this.selectedTenant.length);
    },
    isDirty() {
      return (
        JSON.stringify(this.refTenantList) !== JSON.stringify(this.tenantList)
      );
    },
    groupList() {
      return this.tenantList.map((tenant) => {
        return {
          id: tenant.id,
          name: tenant.name,
        };
      });
    },
  },
  methods: {
    ...mapActions({
      updateTenantGrouping: "admin/updateTenantGrouping",
    }),
    onClose,
    onGroupEdit,
    onGroupDelete,
    onAddGroup,
    onMovieTenant,
    onAddTenant,
    onGroupAddUpdate,
    transformTenantGroupData,
    onEditGroup,
    moveUp,
    moveDown,
    sortTenantList,
    findParent,
    onDelete,
    isDuplicateTenant,
    getNonDuplicateTenants,
    getSiblings,
    isFirstItem,
    isLastItem,
    onSave,
    getGroupNameByTenantId,
  },
  watch: {
    tenantList: {
      immediate: true,
      deep: true,
      async handler(newValue, oldValue) {
        this.movedTenantsId = newValue.flatMap((group) =>
          group.tenants.map((t) => t.id)
        );
      },
    },
  },
};

function getGroupNameByTenantId(tenantId) {
  for (const group of this.tenantList) {
    if (group.tenants?.some((tenant) => tenant.id === tenantId)) {
      return group.name;
    }
  }
  return "not found"; // not found
}

async function onSave() {
  const remainingTenants = this.tenantAuthList
    .filter((tenant) => !tenant.isMoved)
    .map((tenant) => {
      return {
        id: tenant.id,
        name: tenant.name,
      };
    });
  const { tenants, groups } = getFilterGroupTenantList(this.tenantList);
  const allTenants = tenants.concat(remainingTenants);
  const params = {
    tenantid: sessionStorage.getItem("tenantId"),
    tenants: allTenants,
    groups,
  };
  const data = await this.updateTenantGrouping(params);
  if (data.status) {
    this.$store.commit("setAlert", {
      variant: "success",
      message: this.$t("lblTenantGroupSave"),
    });
  }
  this.$emit("input", false);
}

function getFilterGroupTenantList(groupedTenantData) {
  const tenants = groupedTenantData.flatMap((group) =>
    group.tenants.map((tenant) => ({
      id: tenant.id,
      name: tenant.name,
      group_id: group.id,
    }))
  );

  const groups = groupedTenantData.map((group) => ({
    id: group.id,
    name: group.name,
  }));

  return {
    tenants,
    groups,
  };
}

function onMovieTenant() {
  if (!this.selectedGroupTenant.length) {
    this.isErrorShow = true;
    return;
  }

  const targetGroup = this.tenantList.find(
    (f) => f.id === this.selectedGroupTenant[0]
  );
  if (!targetGroup) return;

  const seen = new Set();
  const uniqueSelected = [];
  (this.selectedTenant || []).forEach((t) => {
    if (t && t.id != null && !seen.has(t.id)) {
      seen.add(t.id);
      uniqueSelected.push(t);
    }
  });

  if (!uniqueSelected.length) return; // nothing to move

  const movableTenants = this.getNonDuplicateTenants(
    uniqueSelected,
    targetGroup
  );

  if (!movableTenants.length) {
    alert("Duplicate tenant not allowed");
    return;
  }

  if (Array.isArray(targetGroup.tenants)) {
    targetGroup.tenants = targetGroup.tenants.concat(movableTenants);
  } else {
    targetGroup.tenants = movableTenants.map((tenant, idx) => ({
      ...tenant,
      isGroup: false,
      disabled: true,
      order: idx,
    }));
  }

  this.selectedTenant = [];
}

function onGroupAddUpdate(group) {
  const isExistingGroup = !!group.id;

  if (isExistingGroup) {
    const targetGroup = this.tenantList.find(
      (item) => item.id === group.id && item.isGroup
    );
    if (targetGroup) {
      targetGroup.name = group.name;
    }
  } else {
    const newGroup = {
      id: createUUID(),
      name: group.name,
      isGroup: true,
      order: this.tenantList.length,
      tenants: [],
    };
    this.tenantList.push(newGroup);
  }
}

function moveUp(item) {
  const parent = this.findParent(item);
  const siblings = parent ? parent.tenants : this.tenantList;

  const index = siblings.findIndex((i) => i.id === item.id);
  if (index > 0) {
    const tempOrder = siblings[index].order;
    siblings[index].order = siblings[index - 1].order;
    siblings[index - 1].order = tempOrder;

    siblings.splice(index - 1, 2, siblings[index], siblings[index - 1]);

    this.sortTenantList();
  }
}

function moveDown(item) {
  const parent = this.findParent(item);
  const siblings = parent ? parent.tenants : this.tenantList;

  const index = siblings.findIndex((i) => i.id === item.id);
  if (index < siblings.length - 1) {
    const tempOrder = siblings[index].order;
    siblings[index].order = siblings[index + 1].order;
    siblings[index + 1].order = tempOrder;

    siblings.splice(index, 2, siblings[index + 1], siblings[index]);

    this.sortTenantList();
  }
}

function sortTenantList() {
  this.tenantList.forEach((group) => {
    if (group.tenants) {
      group.tenants.sort((a, b) => a.order - b.order);
    }
  });
  this.tenantList.sort((a, b) => a.order - b.order);
}

function findParent(item) {
  return this.tenantList.find(
    (group) =>
      group.tenants && group.tenants.some((child) => child.id === item.id)
  );
}

function onDelete(item) {
  const parent = this.findParent(item);
  if (parent) {
    parent.tenants = parent.tenants.filter((child) => child.id !== item.id);
  } else {
    this.tenantList = this.tenantList.filter((group) => group.id !== item.id);
  }

  if (Array.isArray(this.selectedGroupTenant)) {
    this.selectedGroupTenant = this.selectedGroupTenant.filter(
      (id) => id !== item.id
    );
  } else if (this.selectedGroupTenant === item.id) {
    this.selectedGroupTenant = null;
  }
}

function isDuplicateTenant(newTenants = [], parentGroup = null) {
  const compare = (a, b) =>
    a.id === b.id || a.name.toLowerCase() === b.name.toLowerCase();

  return newTenants.some((newTenant) => {
    if (parentGroup && parentGroup.tenants) {
      return parentGroup.tenants.some((tenant) => compare(tenant, newTenant));
    } else {
      return this.tenantList.some(
        (item) =>
          compare(item, newTenant) ||
          (item.tenants &&
            item.tenants.some((child) => compare(child, newTenant)))
      );
    }
  });
}

function getNonDuplicateTenants(newTenants = [], parentGroup = null) {
  const compare = (a, b) =>
    a.id === b.id || a.name.toLowerCase() === b.name.toLowerCase();

  return newTenants.filter((newTenant) => {
    if (parentGroup && parentGroup.tenants) {
      return !parentGroup.tenants.some((tenant) => compare(tenant, newTenant));
    } else {
      return !this.tenantList.some(
        (item) =>
          compare(item, newTenant) ||
          (item.tenants &&
            item.tenants.some((child) => compare(child, newTenant)))
      );
    }
  });
}

function getSiblings(item) {
  const parent = this.findParent(item);
  return parent ? parent.tenants : this.tenantList;
}

function isFirstItem(item) {
  const siblings = this.getSiblings(item);
  return siblings.findIndex((i) => i.id === item.id) === 0;
}

function isLastItem(item) {
  const siblings = this.getSiblings(item);
  return siblings.findIndex((i) => i.id === item.id) === siblings.length - 1;
}

function onAddTenant(item) {
  if (this.selectedGroupTenant.length) {
    let fList = this.tenantList.find(
      (f) => f.id === this.selectedGroupTenant[0]
    );
    if (this.isDuplicateTenant(item, fList)) {
      alert("Duplicate tenant not allowed");
      return;
    }
    if (fList.tenants) {
      fList.tenants.push(item);
    } else {
      this.tenantList.push({ ...item, isGroup: false, disabled: true });
    }
  } else {
    if (this.isDuplicateTenant(item)) {
      alert("Duplicate tenant not allowed");
      return;
    }
    this.tenantList.push({ ...item, isGroup: false, disabled: true });
  }
}

function transformTenantGroupData(data) {
  let grp = blankObjectConvertToArray(data.groups);
  const groupsMap = grp.reduce((acc, group, index) => {
    acc[group.id] = {
      id: group.id,
      name: group.name,
      isGroup: true,
      tenants: [],
      order: index,
    };
    return acc;
  }, {});

  const ungroupedTenants = [];

  data.tenants.forEach((tenant) => {
    const groupId = tenant.group_id;
    const tenantEntry = {
      id: tenant.id,
      name: tenant.name,
      disabled: true,
      order: 0,
    };

    if (groupId && groupsMap[groupId]) {
      const group = groupsMap[groupId];
      tenantEntry.order = group.tenants.length;
      group.tenants.push(tenantEntry);
    } else {
      ungroupedTenants.push({
        id: tenant.id,
        name: tenant.name,
        isGroup: false,
        disabled: true,
        order: ungroupedTenants.length,
      });
    }
  });

  const groupedTenants = Object.values(groupsMap);
  return groupedTenants;
}

function onAddGroup(item) {
  this.groupForm = {
    id: null,
    name: null,
  };
  this.showAddGroupDialog = true;
}

function onEditGroup(item) {
  this.groupForm = item;
  this.showAddGroupDialog = true;
}

function onGroupEdit() {
  this.showAddGroupDialog = true;
}

function onGroupDelete() {
  alert("delete");
}

function onClose() {
  this.$emit("input", false);
}
</script>

<style scoped>
.v-application--is-ltr
  .v-list-group--no-action
  > .v-list-group__items
  > .v-list-item {
  padding-left: 50px !important;
}

::v-deep .v-list-item__icon.v-list-group__header__append-icon {
  min-width: 0 !important;
  margin-left: 0px !important;
}

::v-deep
  .tree-node-label.no-active.v-treeview-node__label.v-treeview-node__label--active {
  background-color: transparent !important;
  color: inherit !important;
}

::v-deep
  .theme--light.v-treeview
  .v-treeview-node__children
  .v-treeview-node__root.v-treeview-node--active:hover::before,
.theme--light.v-treeview
  .v-treeview-node__children
  .v-treeview-node__root.v-treeview-node--active::before {
  opacity: 0 !important;
}

::v-deep .v-treeview-node__children .v-treeview-node__root::before {
  background-color: transparent !important;
}

::v-deep
  .theme--light.v-treeview
  .v-treeview-node__children
  .v-treeview-node__root.v-treeview-node--active {
  color: rgba(0, 0, 0, 0.87) !important;
}

::v-deep
  .theme--light.v-treeview
  .v-treeview-node--disabled
  > .v-treeview-node__root
  > .v-treeview-node__content {
  color: rgba(0, 0, 0, 0.87) !important;
}

.chip-text-truncate {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
</style>